// URL ⇄ blade-stack codec for the AR Workbench shell (HUB-275 slice 2). The blade
// stack serializes losslessly to a path so any view is shareable / refresh-safe
// (the resolved decision: path segments under /w, mode+active in the query).
//
// Path:  /w/<key>/<entitySeg>/<key>/<entitySeg>…   entitySeg = encoded id or "-"
// Query: ?mode=focus            (omitted when explorer)
//        &active=<index>        (omitted when the active blade is the last one)
//        &p<i>=<sub-querystring> per-blade params for blade i (omitted when empty)
//
// Only the navigable identity (key + entityId + params) round-trips; display
// strings (title/eyebrow) are re-derived from the entity when the blade mounts, so
// decoded descriptors carry the key as a placeholder title. Ephemeral blades are
// excluded (they live outside the persisted stack).

import type { BladeDescriptor, BladeStackState, PresentationMode } from './types';

export const STACK_BASE = '/w';

/** Sentinel for a bindingless blade (no entityId). Real ids are UUIDs/`type:id`. */
const NO_ENTITY = '-';

/** encodeStack serializes the (non-ephemeral) stack to a path + search string. */
export function encodeStack(state: BladeStackState): string {
  const persistent = state.blades.filter((b) => !b.ephemeral);
  const segments = persistent.flatMap((b) => [
    encodeURIComponent(b.key),
    b.entityId ? encodeURIComponent(b.entityId) : NO_ENTITY,
  ]);
  const path = segments.length ? `${STACK_BASE}/${segments.join('/')}` : STACK_BASE;

  const query = new URLSearchParams();
  if (state.mode === 'focus') query.set('mode', 'focus');
  // `active` is expressed against the PERSISTENT (URL-encoded) blades — the full
  // activeIndex can point at an ephemeral blade that isn't in the URL. If the
  // active blade is ephemeral, default to the last persistent blade (omit).
  const activeBlade = state.blades[state.activeIndex];
  const activePersistent =
    activeBlade && !activeBlade.ephemeral ? persistent.indexOf(activeBlade) : persistent.length - 1;
  if (persistent.length > 0 && activePersistent !== persistent.length - 1) {
    query.set('active', String(activePersistent));
  }
  // Per-blade params, keyed by index (p0, p1, …); value is a sub-querystring.
  persistent.forEach((b, i) => {
    if (b.params && Object.keys(b.params).length > 0) {
      query.set(`p${i}`, new URLSearchParams(b.params).toString());
    }
  });
  const qs = query.toString();
  return qs ? `${path}?${qs}` : path;
}

/** decodeStack parses a path + search string back into a stack state. */
export function decodeStack(pathname: string, search = ''): BladeStackState {
  const blades = decodePath(pathname);

  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const mode: PresentationMode = params.get('mode') === 'focus' ? 'focus' : 'explorer';

  let activeIndex = blades.length - 1;
  const rawActive = params.get('active');
  if (rawActive !== null) {
    const n = Number.parseInt(rawActive, 10);
    if (Number.isInteger(n) && n >= 0 && n < blades.length) activeIndex = n;
  }

  // Per-blade params: p<i>=<sub-querystring> → blade[i].params.
  blades.forEach((b, i) => {
    const raw = params.get(`p${i}`);
    if (!raw) return;
    const sub = new URLSearchParams(raw);
    const p: Record<string, string> = {};
    for (const [k, v] of sub) p[k] = v;
    if (Object.keys(p).length > 0) b.params = p;
  });

  return { blades, activeIndex, mode };
}

function decodePath(pathname: string): BladeDescriptor[] {
  let rest = pathname;
  if (rest.startsWith(STACK_BASE)) rest = rest.slice(STACK_BASE.length);
  const parts = rest.split('/').filter((p) => p.length > 0);

  const blades: BladeDescriptor[] = [];
  for (let i = 0; i + 1 < parts.length; i += 2) {
    const key = decodeURIComponent(parts[i]);
    const entitySeg = parts[i + 1];
    const blade: BladeDescriptor = { key, title: key };
    if (entitySeg !== NO_ENTITY) blade.entityId = decodeURIComponent(entitySeg);
    blades.push(blade);
  }
  return blades;
}
